import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  Portal,
  Text,
} from '@chakra-ui/react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDropdownStyles } from '~/components/shared/componentStyles/Dropdown.style'
import {
  MenuStyleValues,
  useMenuStyles,
} from '~/components/shared/componentStyles/Menu.style'
import { SelectItem } from '~/components/shared/Dropdown/DropdownSelect'
import { MenuOptions } from '~/components/shared/Dropdown/Menu'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { TranslationKeys } from '~/locales/i18next'
import { ZIndex } from '~/types/ThemeTypes'
import { castRef } from '~/utils/refUtils'

type PortaledDropdownSelectProps<TValue extends string | number> = {
  placeholder?: string
  selectedValue?: TValue
  items: SelectItem<TValue>[]
  onSelect?: (value: TValue, index: number) => string | void
  menuOptions?: MenuOptions
  searchable?: boolean
  isDisabled?: boolean
}

export const PortaledDropdownSelect = <TValue extends string | number>({
  placeholder = '',
  selectedValue,
  items = [],
  onSelect,
  menuOptions,
  searchable,
  isDisabled,
}: PortaledDropdownSelectProps<TValue>) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const styles = useDropdownStyles()
  const menuStyles = useMenuStyles()
  const menuRef = useRef<HTMLDivElement>(null)

  const isSearchable = searchable ?? items.length > 10

  const updateAnchorRect = useCallback(() => {
    if (!dropdownRef.current) {
      return
    }
    setAnchorRect(dropdownRef.current.getBoundingClientRect())
  }, [])

  useEffect(() => {
    if (!isOpen) return
    updateAnchorRect()
  }, [isOpen, updateAnchorRect])

  useEffect(() => {
    if (!isOpen) return

    const handleWindowScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return
      }
      setIsOpen(false)
    }

    const handleWindowResize = () => {
      setIsOpen(false)
    }

    window.addEventListener('scroll', handleWindowScroll, true)
    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('scroll', handleWindowScroll, true)
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [isOpen, setIsOpen])

  useOutsideClick({
    ref: castRef(containerRef),
    handler: (e) => {
      // Don't close if clicking inside the portaled menu
      if (menuRef.current?.contains(e.target as Node)) {
        return
      }
      if (isOpen) setIsOpen(false)
    },
    enabled: isOpen,
  })

  const handleSelectedInternal = (value: TValue, _index: number) => {
    const originalIndex = items.findIndex((item) => item.value === value)
    onSelect?.(value, originalIndex)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleMenuToggle = () => {
    if (isDisabled) {
      return
    }
    if (isOpen) {
      setIsOpen(false)
    } else {
      setSearchTerm('')
      setIsOpen(true)
    }
  }

  const getSelectedItemIndex = useCallback(
    (value?: TValue) => {
      if (value !== undefined) {
        return items.findIndex((item) => item.value === value)
      }
      return undefined
    },
    [items],
  )

  const placeHolderText = useMemo(() => {
    return <Box css={styles.placeholderText}>{placeholder}</Box>
  }, [placeholder, styles.placeholderText])

  const selectedText = useMemo(() => {
    const resolvedSelectedIndex = getSelectedItemIndex(selectedValue)
    if (resolvedSelectedIndex === undefined || resolvedSelectedIndex === -1)
      return placeHolderText
    const selectedItem = items[resolvedSelectedIndex]
    if (!selectedItem) return placeHolderText

    // If selectedLabel is provided (React node or string), use it
    if (selectedItem.selectedLabel !== undefined) {
      // If it's a string, wrap it in Text component
      if (typeof selectedItem.selectedLabel === 'string') {
        return (
          <Text lineClamp={1} css={styles.defaultText}>
            {selectedItem.selectedLabel}
          </Text>
        )
      }
      // If it's a React node, use it directly (e.g., StatusPill)
      return selectedItem.selectedLabel
    }

    // Fallback to content if selectedLabel is not provided
    if (selectedItem.content !== undefined) {
      // If it's a string, wrap it in Text component
      if (typeof selectedItem.content === 'string') {
        return (
          <Text lineClamp={1} css={styles.defaultText}>
            {selectedItem.content}
          </Text>
        )
      }
      // If it's a React node, use it directly
      return selectedItem.content
    }

    // Final fallback: use searchLabel as plain text
    if (selectedItem.searchLabel) {
      return (
        <Text lineClamp={1} css={styles.defaultText}>
          {selectedItem.searchLabel}
        </Text>
      )
    }

    return placeHolderText
  }, [
    items,
    selectedValue,
    getSelectedItemIndex,
    placeHolderText,
    styles.defaultText,
  ])

  const extractTextFromReactNode = useCallback(
    (node: React.ReactNode): string => {
      if (typeof node === 'string' || typeof node === 'number') {
        return node.toString()
      }
      if (Array.isArray(node)) {
        return node.map(extractTextFromReactNode).join(' ')
      }
      if (React.isValidElement(node)) {
        const props = node.props as {
          primaryText?: string
          children?: React.ReactNode
        }
        if (props.primaryText) {
          return props.primaryText
        }
        if (props.children) {
          return extractTextFromReactNode(props.children)
        }
      }
      return ''
    },
    [],
  )

  const getItemSearchLabel = useCallback(
    (item: SelectItem<TValue>) => {
      if (item.searchLabel) return item.searchLabel
      if (
        typeof item.content === 'string' ||
        typeof item.content === 'number'
      ) {
        return item.content.toString()
      }
      if (
        typeof item.selectedLabel === 'string' ||
        typeof item.selectedLabel === 'number'
      ) {
        return item.selectedLabel.toString()
      }

      const candidateLabels = [
        extractTextFromReactNode(item.selectedLabel),
        extractTextFromReactNode(item.content),
      ]

      return candidateLabels.find((label) => label.length > 0) ?? ''
    },
    [extractTextFromReactNode],
  )

  const filteredItems = useMemo(() => {
    if (!isSearchable) return items
    const trimmedSearch = searchTerm.trim()
    if (!trimmedSearch) return items

    return items.filter((item) => {
      const label = getItemSearchLabel(item).toLowerCase()
      return label.includes(trimmedSearch.toLowerCase())
    })
  }, [items, searchTerm, isSearchable, getItemSearchLabel])

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
    }
  }, [isOpen])

  useEffect(() => {
    if (isDisabled) {
      setIsOpen(false)
    }
  }, [isDisabled])

  useLayoutEffect(() => {
    const el = menuRef.current
    if (!el || !isOpen) return
    const rect = el.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const EDGE_PADDING = 8

    if (rect.right > viewportWidth - EDGE_PADDING) {
      el.style.left = `${viewportWidth - EDGE_PADDING - rect.width}px`
    }
  }, [isOpen, anchorRect, filteredItems])

  const wrappedItems = useMemo(() => {
    return filteredItems.map((item) => ({
      value: item.value,
      content: item.content,
      clickDisabled: item.clickDisabled,
    }))
  }, [filteredItems])

  return (
    <Flex ref={containerRef} css={styles.container}>
      <Flex
        ref={dropdownRef}
        css={{
          ...styles.menuButton,
          backgroundColor: 'bg.default',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.6 : 1,
          minW: 0,
          _hover: {
            backgroundColor: 'bg.default',
            borderColor: 'border.default',
          },
        }}
        aria-disabled={isDisabled}
        onClick={handleMenuToggle}
      >
        <Box css={{ px: 1, minW: 0, flex: 1, overflow: 'hidden' }}>
          {selectedText}
        </Box>
        <Icon
          as={isOpen ? ChevronUp : ChevronDown}
          css={styles.icon}
          flexShrink={0}
        />
      </Flex>
      {isOpen && anchorRect && (
        <Portal>
          <Box
            ref={menuRef}
            position="fixed"
            zIndex={ZIndex.popover}
            width={menuOptions?.width ?? 'auto'}
            minWidth={`${anchorRect.width}px`}
            left={`${Math.max(8, anchorRect.left)}px`}
            pointerEvents="auto"
            {...(() => {
              const GAP = 6
              const spaceBelow = window.innerHeight - anchorRect.bottom - GAP
              const spaceAbove = anchorRect.top - GAP
              const openAbove = spaceBelow < 150 && spaceAbove > spaceBelow
              return openAbove
                ? { bottom: `${window.innerHeight - anchorRect.top + GAP}px` }
                : { top: `${anchorRect.bottom + GAP}px` }
            })()}
          >
            <Flex
              css={{
                ...menuStyles.container,
                flexDirection: 'column',
                backgroundColor: 'bg.default',
              }}
              data-testid="portaled-dropdown-menu"
            >
              {isSearchable && (
                <Flex css={menuStyles.dividedHeaderContainer}>
                  <InputGroup
                    endElement={
                      <Icon
                        color="border.subtle"
                        boxSize={4}
                        pointerEvents="none"
                      >
                        <Search size="100%" />
                      </Icon>
                    }
                  >
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t(
                        'dropdownSelect.placeholder' as TranslationKeys,
                      )}
                      autoFocus
                    />
                  </InputGroup>
                </Flex>
              )}
              <Flex
                css={{
                  ...menuStyles.scrollerContainer,
                  overflowY: 'auto',
                }}
                maxHeight={
                  menuOptions?.maxHeight ?? MenuStyleValues.defaultMaxHeight
                }
              >
                <Flex direction="column" width="100%">
                  {wrappedItems.length === 0 ? (
                    <Flex
                      css={menuStyles.itemContainer}
                      pt="0.5rem"
                      pb="0.5rem"
                      cursor="default"
                    >
                      <Box css={styles.placeholderText}>
                        {t('dropdownSelect.noResultsFound')}
                      </Box>
                    </Flex>
                  ) : (
                    wrappedItems.map((item, index) => (
                      <Flex
                        key={item.value}
                        css={menuStyles.itemContainer}
                        pt={index ? '0.5rem' : '0px'}
                        cursor={item.clickDisabled ? 'not-allowed' : 'pointer'}
                        opacity={item.clickDisabled ? 0.6 : 1}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (item.clickDisabled) {
                            return
                          }
                          handleSelectedInternal(item.value, index)
                        }}
                      >
                        {item.content}
                      </Flex>
                    ))
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Portal>
      )}
    </Flex>
  )
}
